// When a paint sheet paints again (R2657, ui-map-interface.md §6.1.14).
//
// A layer that paints its looks on a 2D context (`batches.ts` `SheetSink`)
// paints only when the picture changed. This keeper holds the rule, so the
// two layers of the Sapper part read one rule:
//
// - the KEY moved: a row, the look, the reading, the zoom step;
// - a look MOVES with the clock;
// - the view left the painted rectangle.
//
// A paint covers the union of what the layer draws, cut to the view grown
// by `SEA_OBJECTS.sheetMargin` of its size on each side, so a pan inside
// that margin paints nothing again. The scale is the device px of one world
// px, in steps of `SEA_OBJECTS.sheetStep` octaves, and never more than the
// sheet's longest side allows.

import type { CameraUniforms, ViewRect } from '../layer';
import type { SheetSink } from './batches';
import { SEA_OBJECTS } from '../../gen/presentation';
import { zoomOf } from './shared';

/** A world rectangle, as its two corners. */
export interface Box { x0: number; y0: number; x1: number; y1: number }

/** Grows `into` to hold the square of half side `half` round `x, y`. */
export function growBox(into: Box, x: number, y: number, half: number): void {
  into.x0 = Math.min(into.x0, x - half); into.y0 = Math.min(into.y0, y - half);
  into.x1 = Math.max(into.x1, x + half); into.y1 = Math.max(into.y1, y + half);
}

/** An empty box, which any `growBox` replaces. */
export function emptyBox(): Box {
  return clearBox({ x0: 0, y0: 0, x1: 0, y1: 0 });
}

/** Empties `b` in place, so a frame builds its box with no allocation. */
export function clearBox(b: Box): Box {
  b.x0 = b.y0 = Infinity; b.x1 = b.y1 = -Infinity;
  return b;
}

/** The scale of a paint: `pxPerWorld` on the octave grid of `step`. */
export function sheetScale(pxPerWorld: number, step = SEA_OBJECTS.sheetStep): number {
  if (!(pxPerWorld > 0)) return 1;
  const k = Math.round(Math.log2(pxPerWorld) / step) * step;
  return 2 ** k;
}

export class SheetKeeper {
  private painted: Box | null = null;
  private key = '';
  private scale = 0;
  /** How many paints the keeper asked for. The tests read it. */
  paints = 0;

  constructor(private readonly sheet: SheetSink) {}

  /** Forgets the painted picture: the next frame paints. */
  invalidate(): void { this.painted = null; }

  /**
   * One frame. `want` is the union of what the layer draws, or null for
   * nothing; `key` names everything the picture reads besides the clock;
   * `moving` is true while a look moves with the clock. `paint` paints in
   * world px with the zoom the strokes keep their screen size at.
   */
  frame(want: Box | null, cull: ViewRect, view: ViewRect, cam: CameraUniforms, key: string,
    moving: boolean, paint: (c: CanvasRenderingContext2D, zoom: number) => void): void {
    const sheet = this.sheet;
    if (!want || !(want.x1 > want.x0) || !(want.y1 > want.y0)) { sheet.shown = false; return; }
    const sx0 = Math.max(want.x0, cull.x0), sy0 = Math.max(want.y0, cull.y0);
    const sx1 = Math.min(want.x1, cull.x1), sy1 = Math.min(want.y1, cull.y1);
    if (!(sx1 > sx0) || !(sy1 > sy0)) { sheet.shown = false; return; }
    sheet.shown = true;
    const scale = sheetScale(cam.pxPerWorld);
    const p = this.painted;
    const inside = p !== null && sx0 >= p.x0 && sy0 >= p.y0 && sx1 <= p.x1 && sy1 <= p.y1;
    if (inside && !moving && key === this.key && scale === this.scale) return;
    // The view grown by the margin, cut to what the layer draws.
    const mw = (view.x1 - view.x0) * SEA_OBJECTS.sheetMargin, mh = (view.y1 - view.y0) * SEA_OBJECTS.sheetMargin;
    const x0 = Math.max(want.x0, view.x0 - mw), y0 = Math.max(want.y0, view.y0 - mh);
    const x1 = Math.min(want.x1, view.x1 + mw), y1 = Math.min(want.y1, view.y1 + mh);
    const w = x1 - x0, h = y1 - y0;
    let s = scale;
    const longest = Math.max(w, h) * s;
    if (longest > SEA_OBJECTS.sheetMaxPx) s *= SEA_OBJECTS.sheetMaxPx / longest;
    this.key = key; this.scale = scale;
    this.painted = { x0, y0, x1, y1 };
    this.paints++;
    const c = sheet.begin(x0, y0, w, h, s);
    if (!c) return;
    paint(c, zoomOf(cam.zoom));
    sheet.end();
  }
}
